import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const PNGFormatGuide = () => {

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("png-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d6efd 0%, #0dcaf0 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold mb-3 position-relative">
           PNG Optimization Guide for Faster Websites
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            Know exactly when to use PNGs (and when they'll just slow you down)
          </p>
          <div className="mt-4 position-relative">
            <span className="badge bg-light text-primary fs-6 me-2 p-2">
              Web Images
            </span>
            <span className="badge bg-light text-primary fs-6 me-2 p-2">
              Performance
            </span>
            <span className="badge bg-light text-primary fs-6 p-2">
              Real Talk
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-info border-0 rounded-3 shadow-sm"
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4">💡</div>
              <div>
                <p className="fs-5 lh-lg mb-0">
                  I can't tell you how many times I've seen developers tear their hair out over slow-loading websites, 
                  only to discover they're using massive PNG files for photos. Or designers frustrated because their 
                  beautiful logos look like garbage when saved as JPEGs. Let me save you the headache.
                </p>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-primary text-white py-3">
              <h2 className="h4 mb-0 fw-bold">What's in this guide?</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "What makes PNGs special",
                      "Where PNGs absolutely shine",
                      "When PNGs will ruin your day",
                      "PNG-8 vs PNG-24: The real difference",
                      "Where to actually use PNGs",
                      "Pro tips from the trenches",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-primary hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Common myths debunked",
                      "Must-have tools",
                      "Performance must-knows",
                      "Final verdict",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 7}`}
                          className="text-decoration-none text-primary hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div id="section-1" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                1
              </span>
              So what's the big deal with PNGs anyway?
            </h2>
            <p className="text-secondary fs-5">
              PNGs were basically created as the cool, patent-free younger sibling of GIFs. 
              They're like that friend who never loses quality no matter how many times you 
              edit them - seriously resilient.
            </p>
            <div className="row g-3 mt-3">
              {[
                {
                  icon: "🔄",
                  text: "Zero Quality Loss",
                  desc: "Edit and re-save without that gradual degradation JPEGs suffer from"
                },
                {
                  icon: "💎",
                  text: "True Transparency",
                  desc: "Not just on/off transparency - we're talking smooth, gradual transparency"
                },
                {
                  icon: "✂️",
                  text: "Sharp Edges",
                  desc: "Text and logos stay crisp, no fuzzy JPEG artifacts"
                },
                {
                  icon: "🎨",
                  text: "Rich Colors",
                  desc: "Millions of colors means your graphics won't look washed out"
                },
                {
                  icon: "🌐",
                  text: "Universal Support",
                  desc: "Works in all modern browsers - and even most legacy browsers support basic PNG features"
                },
                {
                  icon: "🆓",
                  text: "Completely Free",
                  desc: "No patents, no fees, no lawyers - just good old open source"
                },
              ].map((item, index) => (
                <div key={index} className="col-6 col-md-4 col-lg-2">
                  <div className="p-3 border rounded-3 text-center bg-light hover-lift transition-all h-100">
                    <div className="fs-2 mb-2">{item.icon}</div>
                    <div className="small fw-medium text-dark">{item.text}</div>
                    <div className="text-muted small mt-1">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 */}
          <div id="section-2" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                2
              </span>
              Where PNGs will make you look like a hero
            </h2>
            <div className="row g-4">
              {[
                {
                  icon: "✅",
                  title: "Perfect Quality Preservation",
                  desc: "When you need to save, edit, and re-save without that gradual quality loss that makes JPEGs look like they've been through a blender"
                },
                {
                  icon: "💎",
                  title: "Drop Shadows & Glows",
                  desc: "Those beautiful soft edges and subtle shadows that make designs pop? PNG handles them like a champ"
                },
                {
                  icon: "🔄",
                  title: "Design Iterations",
                  desc: "When you're still tweaking and your client keeps saying 'just one more change' - PNG won't degrade with each save"
                },
                {
                  icon: "🎯",
                  title: "UI Elements & Icons",
                  desc: "Buttons, icons, interface elements - anything that needs to stay sharp and crisp at any size"
                },
              ].map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100">
                    <span className="fs-2 me-3">{item.icon}</span>
                    <div>
                      <h5 className="fw-bold mb-1">{item.title}</h5>
                      <p className="text-secondary mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3 - Where PNG Falls Short */}
          <div id="section-3" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                3
              </span>
              Where PNGs will ruin your performance (and your day)
            </h2>
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm">
                <thead className="table-primary">
                  <tr>
                    <th className="border-0">The Problem</th>
                    <th className="border-0">What Actually Happens</th>
                    <th className="border-0">What to Use Instead</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      problem: "File Size Explosion",
                      impact: "That 500KB photo becomes a 5MB PNG - your users will leave before it loads",
                      alternative: "JPEG for photos, WebP for modern sites"
                    },
                    {
                      problem: "Missing Camera Data",
                      impact: "Loses all the EXIF data photographers care about - dates, settings, locations",
                      alternative: "Stick with JPEG for photography work"
                    },
                    {
                      problem: "No Animation Support",
                      impact: "Standard PNGs don't animate. APNG exists with good browser support but is less common than GIF/WebP",
                      alternative: "GIF for simple, WebP for quality animation"
                    },
                    {
                      problem: "Terrible with Photos",
                      impact: "Compresses photos about as well as a suitcase full of feathers - inefficient",
                      alternative: "JPEG compresses photos 10x better"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.problem}</td>
                      <td className="text-danger">{row.impact}</td>
                      <td>
                        <code>{row.alternative}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 4 - PNG-8 vs PNG-24 */}
          <div id="section-4" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                4
              </span>
              PNG-8 vs PNG-24: Which one should you actually use?
            </h2>
            <div className="row g-4">
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">PNG-8: The Minimalist</h5>
                  </div>
                  <div className="card-body">
                    <p className="fst-italic mb-3">"I only need the basics, and I need them small."</p>
                    <ul className="text-secondary">
                      <li><strong>256 colors max</strong> - like a carefully curated wardrobe</li>
                      <li><strong>Basic transparency</strong> - think cookie cutter shapes</li>
                      <li><strong>Tiny file sizes</strong> - loads in the blink of an eye</li>
                      <li>Perfect for: Simple logos, basic icons, pixel art</li>
                      <li>Think: Your website's favicon, social media icons</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-success me-2">Lightning Fast</span>
                      <span className="badge bg-success">Simple & Clean</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">PNG-24: The Powerhouse</h5>
                  </div>
                  <div className="card-body">
                    <p className="fst-italic mb-3">"I need all the colors and smooth transparency."</p>
                    <ul className="text-secondary">
                      <li><strong>Millions of colors</strong> - the entire rainbow and then some</li>
                      <li><strong>Alpha transparency</strong> - smooth fades and shadows</li>
                      <li><strong>Larger files</strong> - but still reasonable for graphics</li>
                      <li>Perfect for: Complex logos, screenshots, product images</li>
                      <li>Think: Your main logo with drop shadows, app screenshots</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-info me-2">Pro Quality</span>
                      <span className="badge bg-info">Full Features</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="alert alert-warning mt-4" role="alert">
              <strong>Fun fact:</strong> Sometimes a complex PNG-8 can actually be <em>larger</em> than a simple PNG-24. 
              It's counterintuitive, but always worth testing both!
            </div>
          </div>

          {/* Section 5 - Real World Use Cases */}
          <div id="section-5" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                5
              </span>
              Real-World Use Cases: Where PNG Actually Makes Sense
            </h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">✅ Perfect For PNG</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Logos & branding</strong> - crisp edges, transparency support</li>
                      <li><strong>Icons & UI elements</strong> - sharp at any size</li>
                      <li><strong>Screenshots</strong> - text stays readable</li>
                      <li><strong>Infographics</strong> - text and shapes remain sharp</li>
                      <li><strong>Graphics with transparency</strong> - drop shadows, glows</li>
                      <li><strong>Work-in-progress designs</strong> - no quality loss during edits</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white py-3">
                    <h5 className="mb-0 fw-bold">❌ Avoid For PNG</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Photographs</strong> - file size explosion</li>
                      <li><strong>Website hero images</strong> - kills page load time</li>
                      <li><strong>Image galleries</strong> - users will hate you</li>
                      <li><strong>Social media posts</strong> - slow to load in feeds</li>
                      <li><strong>Camera originals</strong> - lose EXIF data</li>
                      <li><strong>Animated content</strong> - PNGs don't move</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0 fw-bold">⚡ Better Alternatives</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>JPEG:</strong> Photos, complex images</li>
                      <li><strong>WebP:</strong> Modern replacement where supported</li>
                      <li><strong>GIF:</strong> Simple animations</li>
                      <li><strong>SVG:</strong> Logos, icons (vector!)</li>
                      <li><strong>AVIF:</strong> Next-gen compression</li>
                      <li><strong>HEIC:</strong> Apple's efficient format</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Actionable Tips */}
          <div id="section-6" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                6
              </span>
              Pro Tips From The Trenches
            </h2>
            <div className="row g-3">
              {[
                "Always use 'Export As' in Photoshop instead of 'Save As' - better compression",
                "Set your screenshot tool to save as PNG by default - text stays crisp",
                "Use PNG-8 for simple graphics, PNG-24 for complex ones with transparency",
                "Run every PNG through TinyPNG before uploading to your site",
                "If a PNG is over 500KB, ask yourself if it really needs to be a PNG",
                "For web, stick with PNGs under 100KB whenever possible",
              ].map((tip, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-center p-3 border rounded-3 bg-light h-100">
                    <div
                      className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "30px", height: "30px" }}
                    >
                      💡
                    </div>
                    <span className="fw-medium">{tip}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 7 - Myth Busting */}
          <div id="section-7" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                7
              </span>
              Common Myths Debunked
            </h2>
            <div
              className="alert alert-warning border-0 rounded-3"
              role="alert"
            >
              <div className="row g-3">
                {[
                  "MYTH: PNG is always the highest quality choice → TRUTH: Only for graphics, terrible for photos",
                  "MYTH: Converting JPEG to PNG improves quality → TRUTH: You can't un-bake a cake - quality loss is permanent",
                  "MYTH: All software handles PNG transparency perfectly → TRUTH: Some email clients and old software show white backgrounds", 
                  "MYTH: PNG is better for all web images → TRUTH: It will murder your site speed if used for photos",
                  "MYTH: PNG files are always larger than JPEG → TRUTH: For simple graphics, PNG can be smaller!",
                ].map((myth, index) => (
                  <div
                    key={index}
                    className="col-md-12 d-flex align-items-start mb-3"
                  >
                    <span className="text-danger me-2 fw-bold fs-5">🚫</span>
                    <span className="fs-6">{myth}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 8 - Tools & Resources */}
          <div id="section-8" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                8
              </span>
              Must-Have Tools That Actually Work
            </h2>
            <div className="list-group shadow-sm">
              {[
                {
                  name: "TinyPNG",
                  desc: "My go-to for PNG compression. Drag and drop, usually cuts file size by 70% without visible quality loss. It's basically magic.",
                  badge: "Daily Driver",
                  link: "https://tinypng.com"
                },
                {
                  name: "ImageOptim (Mac)",
                  desc: "Free, open-source, and brutally effective. It strips all the unnecessary metadata and optimizes the heck out of your PNGs.",
                  link: "https://imageoptim.com"
                },
                {
                  name: "Squoosh by Google",
                  desc: "Web-based and lets you compare different compression settings side-by-side. Great for learning what actually affects quality.",
                  link: "https://squoosh.app"
                },
                {
                  name: "PNGGauntlet (Windows)",
                  desc: "If you're on Windows, this is your best friend. Combines three great optimizers into one simple interface.",
                  link: "https://pnggauntlet.com"
                },
              ].map((tool, index) => (
                <div
                  key={index}
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                >
                  <div>
                    <h6 className="mb-1 fw-bold">
                      <a
                        href={tool.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-dark"
                      >
                        {tool.name}
                      </a>
                    </h6>
                    <p className="mb-1 text-secondary">{tool.desc}</p>
                  </div>
                  {tool.badge && (
                    <span className="badge bg-primary rounded-pill">
                      {tool.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section 9 - Performance Guide */}
          <div id="section-9" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                9
              </span>
              Performance Must-Knows: Don't Tank Your Site Speed
            </h2>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">✅ Do This</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>Use PNG for graphics under 100KB</li>
                      <li>Compress EVERY PNG before uploading</li>
                      <li>Use PNG-8 for simple icons and logos</li>
                      <li>Implement lazy loading for images below the fold</li>
                      <li>Use modern formats like WebP with PNG fallbacks</li>
                      <li>Set up a CDN for faster delivery</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white">
                    <h5 className="mb-0">❌ Avoid This</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li>Don't use PNG for photos - ever</li>
                      <li>Avoid PNG files over 500KB on web pages</li>
                      <li>Don't skip compression - it's not optional</li>
                      <li>Avoid multiple large PNGs on the same page</li>
                      <li>Don't use PNG for background images</li>
                      <li>Don't scale small PNGs up - they get blurry</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="alert alert-info mt-4" role="alert">
              <strong>Real talk:</strong> I once fixed a site that was using 3MB PNGs for product photos. 
              After switching to WebP, the page loaded 4x faster. Your users will thank you.
            </div>
          </div>

          {/* Section 10 - FAQ Section */}
          {/* <div id="section-10" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-primary border-bottom pb-2">
              <span
                className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                10
              </span>
              Your PNG Questions Answered (No Fluff)
            </h2>
            
            <div className="accordion" id="pngFAQ">
              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                    When should I actually use PNG instead of JPEG?
                  </button>
                </h3>
                <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#pngFAQ">
                  <div className="accordion-body">
                    <p>Think of it this way: if it has text, sharp edges, or needs transparency, use PNG. If it's a photo or has lots of color gradients, use JPEG.</p>
                    <p><strong>PNG:</strong> Logos, screenshots, icons, graphics with text</p>
                    <p><strong>JPEG:</strong> Photos, artwork, complex images</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                    Can I make animated PNG files?
                  </button>
                </h3>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#pngFAQ">
                  <div className="accordion-body">
                    <p>Regular PNGs? No animation support - they're strictly still images. There's something called APNG but browser support is spotty.</p>
                    <p>If you need animation, stick with GIF for simple stuff or WebP for better quality.</p>
                  </div>
                </div>
              </div>

            </div>
          </div> */}

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            }}
          >
            <h2 className="h1 fw-bold mb-3 text-primary">
              Ready to stop guessing about image formats?
            </h2>
            <p className="fs-5 text-secondary mb-4">
              Grab some real PNG sample files and see the difference for yourself. No more fuzzy logos or slow websites.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="https://samplefiles.dev/sample-images/sample-png"
                className="btn btn-primary btn-lg px-5 py-3 shadow hover-lift"
                target="_blank"
                rel="noopener noreferrer"
              >
                🖼️ Get PNG Sample Files
              </a>
              <a
                href="#section-1"
                className="btn btn-outline-primary btn-lg px-5 py-3"
              >
                📖 Read This Again
              </a>
            </div>
            <p className="mt-3 text-muted small">
              Various sizes and transparency types - because seeing is believing.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default PNGFormatGuide;

                      {/* "Your PNG questions answered", */}
